/* eslint-disable @typescript-eslint/prefer-for-of */
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FileUpload } from 'primeng/fileupload';
import { ClaimRow } from '../../../models/api/view/claim-view/claim-row';
import { FileUploadConfigValues } from '../../../models/api/view/claim/documentation/documents/file-upload-config-values';
import { FileUploadConfirmation } from '../../../models/api/view/claim/documentation/documents/file-upload-confirmation';
import { fileUploadRequest } from '../../../models/api/view/claim/documentation/documents/file-upload-request';
import { BytesPipe } from '../../../pipes/bytes.pipe';
import { Api } from '../../../services/api/api.service';
import { ViewService } from '../../../services/view/view.service';
import { ButtonComponent } from '../buttons/button.component';
import { IconButtonComponent } from '../buttons/icon-button.component';
import { IconComponent } from '../labels/icon.component';
import { ToastComponent } from '../labels/toast.component';
import { ActionRowComponent } from '../panels/action-row.component';
import { BaseControlComponent } from './base-control.component';

/** Extended File type with upload tracking properties */
interface UploadFile extends File {
  uploadStatus?: 'pending' | 'uploading' | 'success' | 'failed';
  success?: string;
  message?: string;
  fileName?: string;
  objectURL?: SafeUrl;
}

@Component({
  selector: 'app-file-upload',
  imports: [
    FileUpload,
    CommonModule,
    ActionRowComponent,
    ButtonComponent,
    IconComponent,
    IconButtonComponent,
    BytesPipe,
    ToastComponent,
  ],
  template: `
    <p-fileupload
      #fileUpload
      name="files[]"
      (onSelect)="onFilesSelected($event)"
      (onUpload)="onUpload($event)"
      [multiple]="multiple"
      [url]="url"
      [method]="method"
      [mode]="mode"
    >
      <ng-template #header let-files let-chooseCallback="chooseCallback">
        <app-action-row useMobileLayout class="file-upload-header-actions">
          <app-button
            (click)="onChoose($event, chooseCallback)"
            left1
            label="Add files"
          />
        </app-action-row>
      </ng-template>
      <ng-template #empty>
        <div
          class="tw-flex tw-items-center tw-justify-center tw-flex-col tw-border-2 tw-border-dotted tw-min-h-50 tw-pt-2"
        >
          <app-icon
            color="var(--sed-black-80-color)"
            name="fa-light fa-cloud-arrow-up"
            size="120px"
          ></app-icon>
          <p class="tw-mt-2 mb-0">Drag and drop files here to upload.</p>
        </div>
        <app-action-row useMobileLayout>
          <app-button emphasis="Low" (click)="onClear()" right2 label="Clear" />
        </app-action-row>
      </ng-template>
      <ng-template
        #content
        let-files
        let-uploadedFiles="uploadedFiles"
        let-removeFileCallback="removeFileCallback"
        let-removeUploadedFileCallback="removeUploadedFileCallback"
      >
        @if (files?.length > 0 && !hasUploaded && !isClickUpload) {
          <div class="flex flex-wrap gap-4">
            @for (file of files; track file; let i = $index) {
              <div
                class="tw-flex tw-mb-2 tw-items-center tw-gap-4 max-sm:tw-gap-2 tw-w-full"
              >
                <div
                  class="tw-flex-col tw-w-1/3 max-sm:tw-w-auto max-sm:tw-shrink-0"
                >
                  @if (canShowImagePreview(file)) {
                    <img
                      role="presentation"
                      [alt]="file.name"
                      [src]="file.objectURL"
                      width="100"
                      height="50"
                      (error)="onPreviewError(file)"
                    />
                  } @else {
                    <app-icon
                      color="var(--sed-black-80-color)"
                      [name]="getDisplayIcon(file.name)"
                      size="50px"
                    ></app-icon>
                  }
                </div>
                <div
                  class="tw-flex-col tw-w-1/3 max-sm:tw-flex-1 max-sm:tw-min-w-0 max-sm:tw-basis-0"
                >
                  <span
                    class="font-semibold text-ellipsis max-w-60 whitespace-nowrap overflow-hidden tw-font-bold"
                    >{{ getDisplayFileName(file.name) }}</span
                  >
                  <div>{{ file.size | bytes }}</div>
                </div>

                @if (!isClickUpload) {
                  <div
                    class="tw-flex-col tw-w-1/3 max-sm:tw-w-auto max-sm:tw-shrink-0"
                  >
                    <app-action-row>
                      <app-icon-button
                        icon="fa-light fa-x"
                        [ariaLabel]="'Remove ' + getDisplayFileName(file.name)"
                        (click)="
                          onRemoveTemplatingFile(
                            $event,
                            file,
                            removeFileCallback,
                            i
                          )
                        "
                        right1
                      />
                    </app-action-row>
                  </div>
                } @else {
                  <div class="tw-flex-col tw-w-1/3">
                    <div>
                      <b>
                        {{ file.uploadStatus }}
                      </b>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
          <app-action-row useMobileLayout classes="sm:tw-gap-x-0">
            <app-button
              emphasis="Low"
              (click)="onUpload($event)"
              right1
              label="Upload"
              [disabled]="isUploadDisabled()"
            />
            <app-button
              label="Clear"
              (click)="onClear()"
              emphasis="Low"
              right2
            />
          </app-action-row>
        }
        @if (showPostUploadResults()) {
          @for (file of uploadResults(); track file; let i = $index) {
            <div
              class="tw-flex tw-mb-2 tw-items-center tw-gap-4 max-sm:tw-flex-col max-sm:tw-items-stretch max-sm:tw-w-full"
            >
              <div class="tw-flex-col tw-w-1/3 max-sm:tw-w-full">
                <b>{{ getDisplayFileName(file.fileName || file.name) }}</b>
                - {{ file.size | bytes }}
              </div>
              <div class="tw-flex-col tw-w-2/3 max-sm:tw-w-full">
                @if (file.success === 'success') {
                  <app-action-row>
                    <app-icon
                      right1
                      color="var(--sed-status-green-color)"
                      name="fa-light fa-check"
                      size="20px"
                    />
                    <span
                      right2
                      class="tw-font-bold tw-text-[var(--sed-status-green-color)] tw-break-words max-sm:tw-w-full"
                      >{{ file.message }}</span
                    >
                  </app-action-row>
                } @else if (
                  file.uploadStatus === 'pending' ||
                  file.uploadStatus === 'uploading'
                ) {
                  <app-action-row>
                    <span
                      right2
                      class="tw-font-bold tw-text-[var(--sed-black-60-color)] tw-break-words max-sm:tw-w-full"
                      >{{ file.message || 'Uploading...' }}</span
                    >
                  </app-action-row>
                } @else {
                  <app-action-row>
                    <app-icon
                      right1
                      color="var(--sed-status-red-color)"
                      name="fa-light fa-circle-exclamation"
                      size="20px"
                    />
                    <span
                      right2
                      class="tw-font-bold tw-text-[var(--sed-status-red-color)] tw-break-words max-sm:tw-w-full"
                      >{{ file.message || 'Upload failed' }}</span
                    >
                  </app-action-row>
                }
              </div>
            </div>
          }

          <app-action-row useMobileLayout classes="sm:tw-gap-x-0">
            <app-button
              emphasis="Low"
              (click)="onUpload($event)"
              right1
              label="Upload"
              [disabled]="isUploadDisabled()"
            />
            <app-button
              label="Clear"
              (click)="onClear()"
              emphasis="Low"
              right2
            />
          </app-action-row>
        }
        @if (maxReached) {
          <app-toast
            center1
            type="Error"
            [dismiss]="true"
            [dismissSeconds]="10"
            (dismissed)="maxReached = false"
          >
            <ng-content>Maximum file count is : {{ maxFiles }}</ng-content>
          </app-toast>
        }
        @if (duplicates.length > 0) {
          <app-toast
            center1
            type="Error"
            [dismiss]="true"
            [dismissSeconds]="10"
            (dismissed)="duplicates = []"
          >
            <ng-content>Duplicate file found.</ng-content>
          </app-toast>
        }
        @if (passwordProtected) {
          <app-toast
            center1
            type="Error"
            [dismiss]="true"
            [dismissSeconds]="10"
            (dismissed)="passwordProtected = false"
          >
            <ng-content
              >Error: {{ fileNamePwd }} is password protected.</ng-content
            >
          </app-toast>
        }
        @if (passwordProtectedDoc) {
          <app-toast
            center1
            type="Error"
            [dismiss]="true"
            [dismissSeconds]="10"
            (dismissed)="passwordProtectedDoc = false"
          >
            <ng-content
              >Error: {{ fileNamePwdDoc }} is password protected.</ng-content
            >
          </app-toast>
        }
        @if (maxDocFileSize) {
          <app-toast
            center1
            type="Error"
            [dismiss]="true"
            [dismissSeconds]="10"
            (dismissed)="maxDocFileSize = false"
          >
            <ng-content>Error: {{ fileNameExtDoc }} is too large.</ng-content>
          </app-toast>
        }
        @if (maxMediaFileSize) {
          <app-toast
            center1
            type="Error"
            [dismiss]="true"
            [dismissSeconds]="10"
            (dismissed)="maxMediaFileSize = false"
          >
            <ng-content>Error: {{ fileNameExtMedia }} is too large.</ng-content>
          </app-toast>
        }
        @if (invalidFileType) {
          @for (name of invalidFileNames; track name) {
            <app-toast
              center1
              type="Error"
              [dismiss]="true"
              [dismissSeconds]="10"
              (dismissed)="invalidFileType = false; invalidFileNames = []"
            >
              <ng-content> Error: {{ name }} is not supported </ng-content>
            </app-toast>
          }
        }
        @if (invalidSpecialFileType) {
          <app-toast
            center1
            type="Error"
            [dismiss]="true"
            [dismissSeconds]="10"
            (dismissed)="invalidSpecialFileType = false"
          >
            <ng-content>
              Error: {{ invalidSpecialFileName }} : File names must only contain
              space, alphanumeric characters, underscores and hyphens.
            </ng-content>
          </app-toast>
        }
        @if (maliciousFileType) {
          <app-toast
            center1
            type="Error"
            [dismiss]="true"
            [dismissSeconds]="10"
            (dismissed)="maliciousFileType = false"
          >
            <ng-content>
              Error: {{ maliciousFileName }} : {{ maliciousFileExt }} is not
              supported .
            </ng-content>
          </app-toast>
        }
        @if (truncateFileType) {
          <app-toast
            center1
            type="Error"
            [dismiss]="true"
            [dismissSeconds]="10"
            (dismissed)="truncateFileType = false"
          >
            <ng-content>
              Error: {{ truncateFileName }} File name must be between
              {{ minLengthFileName }} and {{ maxLengthFileName }}
              characters.
            </ng-content>
          </app-toast>
        }
      </ng-template>

      <ng-template #file></ng-template>
    </p-fileupload>
  `,
  styles: `
    /* Header is flex-shrink-wrapped; full-width host enables mobile button stretch. */
    @media (max-width: 639px) {
      :host ::ng-deep .p-fileupload-header .file-upload-header-actions {
        flex: 1 1 100%;
        width: 100%;
      }
    }
  `,
})
export class FileUploadComponent
  extends BaseControlComponent
  implements OnInit
{
  private view = inject(ViewService);
  private api = inject(Api);
  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);

  private previewFailedKeys = new Set<string>();

  @ViewChild('fileUpload') fileUploadChild: FileUpload;
  fileUploadConfirmation: FileUploadConfirmation;

  maxReached = false;
  duplicates: string[] = [];
  passwordProtected = false;
  passwordProtectedDoc = false;
  fileName = '';
  fileNamePwd = '';
  fileNamePwdDoc = '';
  fileNameExtDoc = '';
  fileNameExtMedia = '';
  maxDocFileSize = false;
  maxMediaFileSize = false;
  invalidFileType = false;
  invalidFileNames: string[] = [];
  invalidSpecialFileType = false;
  invalidSpecialFileName = '';
  truncateFileType = false;
  truncateFileName = '';
  maliciousFileType = false;
  maliciousFileName = '';
  maliciousFileExt = '';
  maxLengthFileName = 55;
  minLengthFileName = 3;
  isClickUpload = false;

  @Input()
  claim: ClaimRow;

  @Input()
  multiple = true;

  @Input()
  url = '';

  @Input()
  mode: AllowedModes = 'basic';

  @Input()
  method: AllowedMethods = 'post';

  @Input()
  maxFiles = 10;

  @Input()
  acceptedFileTypes =
    '.doc, .gif, .jpg, .jpeg, .pdf, .rtf, .tif, .txt, .png, .docx,  .mpeg, .mp3, .mp4, .wmv, .wav, .avi, .mkv, .mov, .m4v, .dxa, .mpg4, .asf';

  hasUploaded = false;
  enableUpdateBtn = false;
  fileUploadRequest: fileUploadRequest;
  fileUploadConfigValues: FileUploadConfigValues;

  whileUploading = false;

  async ngOnInit() {
    this.fileUploadConfigValues = await this.api.document.getConfigValues();
  }
  async onUpload($event: any) {
    this.resetValidationFlags();
    this.claim = this.view.currentClaim();
    const extdoc = this.csvToArray(
      this.fileUploadConfigValues.fileUploadExtDoc,
    );
    const extmedia = this.csvToArray(
      this.fileUploadConfigValues.fileUploadExtVideo,
    );
    const allowedExtensions = [...extdoc, ...extmedia];

    let count = 1;
    const files = this.fileUploadChild.files;
    const tempUpload: UploadFile[] = [];
    for (let i = 0; i < files.length; i++) {
      const element = files[i];
      const isTruncate = await this.isTruncateFileName(element.name);
      const isSpecialChar = this.stripSpecialCharsFromFileName(element.name);
      const isMalicious = await this.verifyFileMagicNumber(element);

      if (isTruncate) {
        this.truncateFileType = true;
        this.truncateFileName = element.name;
        files.splice(i, 1);
        i--;
        continue;
      }

      if (isSpecialChar) {
        this.invalidSpecialFileType = true;
        this.invalidSpecialFileName = element.name;
        files.splice(i, 1);
        i--;
        continue;
      }

      if (!isMalicious) {
        this.maliciousFileType = true;
        this.maliciousFileName = element.name;
        this.maliciousFileExt = element.name.split('.').pop() ?? '';
        files.splice(i, 1);
        i--;
        continue;
      }

      const ext = element.name.split('.').pop().toLowerCase();

      // File type validation
      if (!allowedExtensions.includes(ext)) {
        this.invalidFileType = true;
        this.invalidFileNames.push(element.name + ': ' + ext);
        files.splice(i, 1);
        i--;
        continue;
      }

      if (files.length > this.maxFiles) {
        this.maxReached = true;
      }

      this.duplicates = this.findDuplicateFileNames(files);

      // PDF password protection check
      if (ext === 'pdf') {
        this.passwordProtected = await this.checkPdfPasswordProtected(element);
        if (this.passwordProtected) {
          this.fileNamePwd = element.name;
          files.splice(i, 1);
          i--;
          continue;
        }
      }

      // DOCX/XLSX/DOC/XLS password protection check
      if (ext === 'docx' || ext === 'xlsx' || ext === 'doc' || ext === 'xls') {
        this.passwordProtectedDoc = await new Promise((resolve) => {
          this.checkOfficePasswordProtected(element, (isProtected) => {
            resolve(isProtected);
          });
        });
        if (this.passwordProtectedDoc) {
          this.fileNamePwdDoc = element.name;
          files.splice(i, 1);
          i--;
          continue;
        }
      }

      if (extdoc.find((x) => x === ext)) {
        const maxDocSize = this.fileUploadConfigValues.fileUploadLimitDoc;
        if (element.size > this.mbToBytes(maxDocSize)) {
          this.maxDocFileSize = true;
          this.fileNameExtDoc = element.name;
          files.splice(i, 1);
          i--;
          continue;
        }
      }

      if (extmedia.find((x) => x === ext)) {
        const maxvideoSize = this.fileUploadConfigValues.fileUploadLimitVideo;
        if (element.size > this.mbToBytes(maxvideoSize)) {
          this.maxMediaFileSize = true;
          this.fileNameExtMedia = element.name;
          files.splice(i, 1);
          i--;
          continue;
        }
      }

      this.fileName = element.name;

      tempUpload.push(element as UploadFile);
      count++;
    }

    this.fileUploadChild.uploadedFiles = tempUpload;
    if (this.maxReached || this.duplicates.length > 0) {
      return;
    }

    if (this.fileUploadChild.uploadedFiles.length === 0) {
      this.hasUploaded = false;
      this.isClickUpload = false;
      this.whileUploading = false;
      return;
    }

    if (this.fileUploadChild.uploadedFiles.length > 0) {
      this.isClickUpload = true;
      this.fileUploadChild.files = [];
      // Set status to 'pending' for each file after validation, before upload starts
      this.fileUploadChild.uploadedFiles.forEach(
        (file) => ((file as UploadFile).uploadStatus = 'pending'),
      );
      for (let i = 0; i < this.fileUploadChild.uploadedFiles.length; i++) {
        this.whileUploading = true;
        const file = this.fileUploadChild.uploadedFiles[i] as UploadFile;
        file.uploadStatus = 'uploading';
        try {
          const result: FileUploadConfirmation =
            await this.uploadDocumentsOnServer(file);
          file.success = result.code;
          file.message = result.statusDescription;
          file.fileName = result.fileName || file.name;
          file.uploadStatus = result.code === 'success' ? 'success' : 'failed';
        } catch (error: unknown) {
          file.success = 'failed';
          file.message = this.uploadErrorMessage(error);
          file.fileName = file.fileName || file.name;
          file.uploadStatus = 'failed';
        }
        this.fileUploadChild.uploadedFiles = [
          ...this.fileUploadChild.uploadedFiles,
        ];
      }
      this.whileUploading = false;

      this.hasUploaded = true;
    }
  }

  private resetValidationFlags(): void {
    this.maxReached = false;
    this.duplicates = [];
    this.passwordProtected = false;
    this.passwordProtectedDoc = false;
    this.maxDocFileSize = false;
    this.maxMediaFileSize = false;
    this.invalidFileType = false;
    this.invalidFileNames = [];
    this.invalidSpecialFileType = false;
    this.truncateFileType = false;
    this.maliciousFileType = false;
  }

  private uploadErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const bodyMessage =
        typeof error.error === 'object' && error.error !== null
          ? (error.error as { message?: string }).message
          : undefined;
      return (
        bodyMessage || error.statusText || error.message || 'Upload failed'
      );
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'Upload failed';
  }

  async uploadDocumentsOnServer(
    uploadedFiles: File,
  ): Promise<FileUploadConfirmation> {
    this.isClickUpload = true;
    const file = uploadedFiles;
    const formData = new FormData();
    formData.append('lineOfBusiness', this.claim.lineOfBusiness);
    formData.append(
      'vendorId',
      this.claim.vendorId == undefined ||
        this.claim.vendorId == null ||
        this.claim.vendorId == 'undefined'
        ? '0'
        : this.claim.vendorId,
    );
    formData.append(
      'procUnit',
      this.claim.procUnit == undefined ||
        this.claim.procUnit == null ||
        this.claim.procUnit == 'undefined'
        ? '0'
        : this.claim.procUnit,
    );
    formData.append('fileNumber', this.claim.fileNumber);
    formData.append(
      'eventNumber',
      this.claim.eventNumber == undefined ||
        this.claim.eventNumber == null ||
        this.claim.eventNumber == 'undefined'
        ? '0'
        : this.claim.eventNumber,
    );
    formData.append('contractNumber', this.claim.contractNumber);
    formData.append('files', file, file.name);

    const confirmation = await this.api.document.uploadDocument(
      this.claim.recordSource,
      this.claim.recordId,
      formData,
    );
    if (!confirmation?.[0]) {
      throw new Error('Upload failed: no confirmation returned from server');
    }
    return confirmation[0];
  }

  findDuplicateFileNames(files: File[]): string[] {
    const nameCount: Record<string, number> = {};
    files.forEach((file) => {
      // Use the full name including extension
      const fullName = file.name;
      nameCount[fullName] = (nameCount[fullName] || 0) + 1;
    });
    // Return file names (with extension) that appear more than once
    return Object.keys(nameCount).filter((name) => nameCount[name] > 1);
  }

  checkPdfPasswordProtected(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function (e: any) {
        const text = new TextDecoder().decode(e.target.result);
        resolve(text.includes('/Encrypt'));
      };
      // Read the first 1KB of the file
      reader.readAsArrayBuffer(file.slice(0, 1024));
    });
  }

  checkOfficePasswordProtected(
    file: File,
    callback: (isProtected: boolean) => void,
  ): void {
    const ext = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();
    reader.onload = function (e: any) {
      const arr = new Uint8Array(e.target.result);
      let isProtected = false;
      if (ext === 'docx' || ext === 'xlsx') {
        // DOCX/XLSX: ZIP files, look for text markers
        const text = new TextDecoder().decode(arr);
        isProtected =
          text.includes('EncryptedPackage') ||
          text.toLowerCase().includes('encryption');
      } else if (ext === 'doc') {
        // DOC: Scan for FilePass (0x2F 0x00), Document Encryption (0x13 0x00) in first 512 bytes
        const scanLimit = Math.min(arr.length - 1, 512);
        for (let i = 0; i < scanLimit; i++) {
          if (
            (arr[i] === 0x2f && arr[i + 1] === 0x00) ||
            (arr[i] === 0x13 && arr[i + 1] === 0x00)
          ) {
            isProtected = true;
            break;
          }
        }
        if (!isProtected) {
          // Check for ASCII 'FilePass' marker in first 8KB
          const text = new TextDecoder().decode(arr.slice(0, 8192));
          if (text.includes('FilePass')) {
            isProtected = true;
          }
        }
      } else if (ext === 'xls') {
        // XLS: Binary format, check header bytes
        // Password protection: 0x13 == 0x00 and 0x14 == 0x01
        // See: https://interoperability.blob.core.windows.net/files/MS-XLS/%5bMS-XLS%5d.pdf (2.2.2)
        if (arr.length > 0x14) {
          isProtected = arr[0x13] === 0x00 && arr[0x14] === 0x01;
        }
      }
      callback(isProtected);
    };
    // Read up to 1MB for DOC, 100 bytes for XLS, 10KB for DOCX/XLSX
    if (ext === 'doc') {
      reader.readAsArrayBuffer(file.slice(0, 1048576));
    } else if (ext === 'xls') {
      reader.readAsArrayBuffer(file.slice(0, 100));
    } else {
      reader.readAsArrayBuffer(file.slice(0, 10240));
    }
  }

  onChoose($event: any, callback: any) {
    if (this.hasUploaded) {
      this.onClear();
      this.hasUploaded = false;
    }
    callback();
  }

  onRemoveTemplatingFile(
    $event: any,
    file: any,
    removeFileCallback: any,
    index: any,
  ) {
    removeFileCallback($event, index);
  }

  onClear() {
    this.fileUploadChild.files = [];
    this.fileUploadChild.uploadedFiles = [];
    this.hasUploaded = false;
    this.isClickUpload = false;
    this.whileUploading = false;
    this.previewFailedKeys.clear();
    this.resetValidationFlags();
  }

  onFilesSelected(event: { currentFiles: File[] }): void {
    for (const file of event.currentFiles) {
      this.ensureImagePreviewUrl(file as UploadFile);
    }
  }

  canShowImagePreview(file: UploadFile): boolean {
    return (
      !!file.objectURL && !this.previewFailedKeys.has(this.filePreviewKey(file))
    );
  }

  onPreviewError(file: UploadFile): void {
    this.previewFailedKeys.add(this.filePreviewKey(file));
    this.cdr.markForCheck();
  }

  private filePreviewKey(file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }

  private ensureImagePreviewUrl(file: UploadFile): void {
    const ext = this.getFileExtension(file.name);
    if (!['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      return;
    }

    const existingUrl = this.safeUrlValue(file.objectURL);
    if (existingUrl?.startsWith('data:')) {
      return;
    }

    // PrimeNG uses blob: URLs, which QA/production CSP blocks for img-src.
    this.revokeBlobPreviewUrl(file.objectURL);
    file.objectURL = undefined;

    const reader = new FileReader();
    reader.onload = (): void => {
      const result = reader.result;
      if (typeof result !== 'string') {
        this.previewFailedKeys.add(this.filePreviewKey(file));
        this.cdr.markForCheck();
        return;
      }
      file.objectURL = this.sanitizer.bypassSecurityTrustUrl(result);
      this.cdr.markForCheck();
    };
    reader.onerror = (): void => {
      this.previewFailedKeys.add(this.filePreviewKey(file));
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  private safeUrlValue(url: SafeUrl | undefined): string | undefined {
    if (!url) {
      return undefined;
    }
    return (url as { changingThisBreaksApplicationSecurity?: string })
      .changingThisBreaksApplicationSecurity;
  }

  private revokeBlobPreviewUrl(url: SafeUrl | undefined): void {
    const value = this.safeUrlValue(url);
    if (value?.startsWith('blob:')) {
      URL.revokeObjectURL(value);
    }
  }

  private getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === fileName.length - 1) {
      return '';
    }
    return fileName.substring(lastDot + 1).toLowerCase();
  }

  mbToBytes(mb: string): number {
    const mbValue = parseFloat(mb);
    if (isNaN(mbValue)) {
      throw new Error('Invalid MB value');
    }
    return mbValue * 1024 * 1024;
  }

  csvToArray(csv: string): string[] {
    if (!csv || typeof csv !== 'string') {
      return [];
    }
    // Config may be Classic-cased (HTML, PDF) or lowercase (html, pdf).
    // File extensions are always compared lowercased — normalize the allow-list too.
    return csv
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0);
  }

  isTruncateFileName(fileName: string): boolean {
    const parts = fileName.split('.');
    if (parts.length < 2) {
      return true;
    }
    const base = parts.join('.');
    if (base.length > this.maxLengthFileName) {
      return true;
    }
    if (base.length < this.minLengthFileName) {
      return true;
    }
    return false;
  }

  stripSpecialCharsFromFileName(fileName: string): boolean {
    // Only allow alphanumeric, underscore, hyphen, dot, and space
    return /[^a-zA-Z0-9_.\- ]/.test(fileName);
  }

  async verifyFileMagicNumber(file: File): Promise<boolean> {
    // Map of file extensions to magic numbers (hex strings)
    const magicNumbers: Record<string, string[]> = {
      gif: [
        '47494638',
        'ffd8ff',
        '7b5c7274',
        '49492a00',
        '4d4d002a',
        '89504e47',
      ],
      jpg: [
        '47494638',
        'ffd8ff',
        '7b5c7274',
        '49492a00',
        '4d4d002a',
        '89504e47',
      ],
      jpeg: [
        '47494638',
        'ffd8ff',
        '7b5c7274',
        '49492a00',
        '4d4d002a',
        '89504e47',
      ],
      rtf: [
        '47494638',
        'ffd8ff',
        '7b5c7274',
        '49492a00',
        '4d4d002a',
        '89504e47',
      ],
      tif: [
        '47494638',
        'ffd8ff',
        '7b5c7274',
        '49492a00',
        '4d4d002a',
        '89504e47',
      ],
      tiff: [
        '47494638',
        'ffd8ff',
        '7b5c7274',
        '49492a00',
        '4d4d002a',
        '89504e47',
      ],
      png: [
        '47494638',
        'ffd8ff',
        '7b5c7274',
        '49492a00',
        '4d4d002a',
        '89504e47',
      ],
      pdf: ['25504446'],
      doc: ['d0cf11e0'],
      docx: ['504b0304'],
      mpeg: [
        '000001ba',
        '000001b3',
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
      ],
      mpg: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],
      mp4: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],
      mkv: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],
      m4v: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],
      mpg4: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],
      avi: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],
      mov: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],
      asf: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],
      mp3: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],
      wmv: [
        '000001ba',
        '000001b3',
        '66747970',
        '00000018',
        '00000020',
        '1a45dfa3',
        '00000020',
        '52494646',
        '66747971',
        '00000014',
        '3026b2758e66cf11',
        '3026b275',
        '494433',
        'fffb',
      ],

      wav: ['52494646'],
      // Add more as needed
    };
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !magicNumbers[ext]) return true; // If not supported, skip check
    // Read first 4 bytes (or more for some types)
    const readBytes = ['mp4', 'm4v', 'mov', 'mpg4', 'mkv'].includes(ext)
      ? 16
      : 4;
    const buffer = await file.slice(0, readBytes).arrayBuffer();
    const bytes = new Uint8Array(buffer);
    // Convert bytes to hex string
    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    // Check if any magic number matches
    if (magicNumbers[ext].some((magic) => hex.startsWith(magic))) {
      return true;
    }
    if (
      ['mp4', 'm4v', 'mov', 'mpg4'].includes(ext) &&
      hex.substring(8, 16) === '66747970'
    ) {
      return true;
    }
    return false;
  }

  sanitizeFileName(fileName: string): string {
    return fileName.replace(/[^a-zA-Z0-9_.-]/g, '');
  }

  getDisplayFileName(fileName: string, maxLength = 20): string {
    if (!fileName || fileName.length <= maxLength) return fileName;
    const extIndex = fileName.lastIndexOf('.');
    if (extIndex === -1 || extIndex < 5) {
      // No extension or very short name
      return fileName.substring(0, maxLength - 3) + '...';
    }
    const base = fileName.substring(0, 20);
    const ext = fileName.substring(extIndex - 4, fileName.length);
    return `${base}...${ext}`;
  }

  getDisplayIcon(fileName: string): string {
    if (!fileName) return 'fa-light fa-file';
    const ext = this.getFileExtension(fileName);
    if (!ext) return 'fa-light fa-file';
    const iconMap: Record<string, string> = {
      tif: 'fa-light fa-file-certificate',
      tiff: 'fa-light fa-file-certificate',
      gif: 'fa-light fa-gif',
      doc: 'fa-light fa-file-doc',
      docx: 'fa-light fa-file-doc',
      rtf: 'fa-light fa-file-invoice',
      csv: 'fa-light fa-file-csv',
      pdf: 'fa-light fa-file-pdf',
      xls: 'fa-light fa-file-xls',
      xlsx: 'fa-light fa-file-xls',
      txt: 'fa-light fa-file-lines',
      jpg: 'fa-light fa-file-jpg',
      jpeg: 'fa-light fa-file-jpg',
      png: 'fa-light fa-file-png',
      zip: 'fa-light fa-file-zip',
      mp3: 'fa-light fa-file-audio',
      dxa: 'fa-light fa-file-audio',
      wav: 'fa-light fa-file-audio',
      asf: 'fa-light fa-file-video',
      mpg4: 'fa-light fa-file-video',
      mpeg: 'fa-light fa-file-video',
      wmv: 'fa-light fa-file-video',
      m4v: 'fa-light fa-file-video',
      mp4: 'fa-light fa-file-video',
      avi: 'fa-light fa-file-video',
      mkv: 'fa-light fa-file-video',
      mov: 'fa-light fa-file-video',
      html: 'fa-light fa-file-code',
      htm: 'fa-light fa-file-code',
    };
    return iconMap[ext] ?? 'fa-light fa-file';
  }

  uploadResults(): UploadFile[] {
    return (this.fileUploadChild?.uploadedFiles ?? []) as UploadFile[];
  }

  showPostUploadResults(): boolean {
    return (
      this.uploadResults().length > 0 &&
      (this.hasUploaded || this.isClickUpload)
    );
  }

  isUploadDisabled(): boolean {
    return this.hasUploaded || this.whileUploading;
  }
}

export type AllowedModes = 'basic' | 'advanced';
export type AllowedMethods = 'post' | 'put';

export class Files {
  fileName: string;
  file?: File;
  status?: string;
  statusMessage?: string;
  date?: Date;
  progress?: number;
  dcn: string;
  created: string;
  sACHeaderUID: string;
  readStatus: string;
  newlyAdded?: boolean;
}
